import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import { Context } from 'contexts/Modals'
import { RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { ReactNode, useContext } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { modalColors } from 'theme/modal/colors'

const ModalHeader: React.FC<
  {
    colorTheme: keyof typeof modalColors
    title?: string | ReactNode
    hideClose?: boolean
    hideStroke?: boolean
    onBack?: () => void
    useBackIcon?: boolean
    titleAlign?: 'left' | 'center'
    onDismiss?: () => void
  } & Omit<FlexProps, 'title'>
> = ({
  title,
  color,
  onBack,
  colorTheme,
  hideClose,
  hideStroke,
  titleAlign = 'center',
  useBackIcon,
  onDismiss: onDismissProps,
  ...props
}) => {
  const { onDismiss } = useContext(Context)
  const isBackWithText = onBack && !useBackIcon
  const isBackWithIcon = onBack && useBackIcon

  return (
    <StyledContainer
      colorTheme={colorTheme}
      className="wrap-header-container"
      minHeight="24px"
      $hideStroke={hideStroke}
      $minimal={!title && !onBack}
      {...props}
    >
      <RowMiddle width="100%" position="relative">
        {title && typeof title === 'string' ? (
          <Text
            width="100%"
            color={color || `modal.${colorTheme}.titleText`}
            fontWeight={700}
            fontSize="16px"
            textAlign={titleAlign}
            className="header-title"
            pl={isBackWithText ? '60px' : isBackWithIcon || titleAlign === 'center' ? '24px' : '0px'}
            pr={isBackWithText ? '60px' : isBackWithIcon || !hideClose ? '24px' : '0px'}
          >
            <Trans>{title}</Trans>
          </Text>
        ) : (
          title
        )}

        <RowMiddle
          position="absolute"
          right="0"
          left="0"
          top="0"
          maxHeight="19px"
          justifyContent={isBackWithText || isBackWithIcon ? 'space-between' : 'flex-end'}
        >
          {isBackWithText ? (
            <StyledBack colorTheme={colorTheme} onClick={() => onBack()} mr="6px">
              <Icons.BackWithTextIcon width="8px" height="20px" />
              <Text fontWeight={400} fontSize="16px" ml="5px">
                <Trans>Back</Trans>
              </Text>
            </StyledBack>
          ) : isBackWithIcon ? (
            <RowCenter height="24px" width="24px" minWidth="24px" minHeight="24px" position="relative">
              <StyledButtonIcon
                colorTheme={colorTheme}
                onClick={() => onBack()}
                position="relative"
                style={{
                  transform: 'translateX(-20%)',
                }}
              >
                <Icons.BackIcon width="22px" height="22px" />
                <Box
                  position={['absolute', 'absolute', 'unset']}
                  left="-20px"
                  top="-20px"
                  right="-20px"
                  bottom="-20px"
                  borderRadius="50%"
                />
              </StyledButtonIcon>
            </RowCenter>
          ) : null}

          {!hideClose && (
            <RowCenter height="24px" width="0" position="relative">
              <StyledButtonIcon
                position="absolute"
                right="0"
                top="0"
                p="0 !important"
                style={{
                  transform: 'translateX(20%)',
                }}
                className="modal-closebutton"
                id="modal-closebutton"
                onClick={() => {
                  if (onDismissProps) onDismissProps()
                  onDismiss()
                }}
                colorTheme={colorTheme}
              >
                <Icons.CloseIcon width="22" height="22" />
                <Box
                  position={['absolute', 'absolute', 'unset']}
                  left="-20px"
                  top="-20px"
                  right="-20px"
                  bottom="-20px"
                  borderRadius="50%"
                />
              </StyledButtonIcon>
            </RowCenter>
          )}
        </RowMiddle>
      </RowMiddle>
    </StyledContainer>
  )
}

const StyledContainer = styled(Flex)<
  FlexProps & { $minimal: boolean; colorTheme: keyof typeof modalColors; $hideStroke?: boolean }
>`
  display: ${({ display }) => display || 'flex'};
  align-items: ${({ alignItems }) => alignItems || 'center'};
  justify-content: space-between;
  position: sticky;
  padding: 20px;
  ${({ $minimal, colorTheme, theme, $hideStroke }) =>
    !$minimal && !$hideStroke && `border-bottom: 1px solid ${theme.modal.colors[colorTheme].stroke};`};
`

export const StyledButtonIcon = styled(Box)<{ colorTheme: keyof typeof modalColors }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;

  > svg {
    min-width: 22px;
    min-height: 22px;
    margin-left: 0 !important;
    fill: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].titleText};
  }
  &:hover {
    background-color: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].modalCard};
  }
`

const StyledBack = styled(RowCenter)<{ colorTheme: keyof typeof modalColors }>`
  cursor: pointer;

  ${Text} {
    color: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].textSecondary};
  }

  > svg {
    margin-left: 0 !important;
    fill: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].textSecondary};
  }

  &:hover {
    ${Text} {
      color: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].titleText};
    }

    > svg {
      margin-left: 0 !important;
      fill: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].titleText};
    }
  }
`

export default ModalHeader
